import { ref, onUnmounted } from 'vue';

export const QR_SECONDS_PER_REFRESH = 10;
export const QR_MAX_AUTO_REFRESHES = 10;

// Evolution/Baileys QR codes go stale after ~60s. Keeps one fresh automatically every 10s, up to
// 10 times (~100s) - past that a stuck/backgrounded tab is more likely than someone still trying
// to scan, so it stops and waits for an explicit manual refresh instead of polling forever unseen.
export function useQrCodeAutoRefresh(fetchQrcode) {
  const qrcodeBase64 = ref('');
  const isLoadingQrcode = ref(false);
  const secondsRemaining = ref(QR_SECONDS_PER_REFRESH);
  const autoRefreshCount = ref(0);
  const needsManualRefresh = ref(false);
  let tickTimer = null;

  const stop = () => {
    if (tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };

  const fetchAndSet = async () => {
    isLoadingQrcode.value = true;
    try {
      qrcodeBase64.value = await fetchQrcode();
    } finally {
      isLoadingQrcode.value = false;
    }
  };

  const tick = async () => {
    secondsRemaining.value -= 1;
    if (secondsRemaining.value > 0) return;

    autoRefreshCount.value += 1;
    if (autoRefreshCount.value >= QR_MAX_AUTO_REFRESHES) {
      stop();
      needsManualRefresh.value = true;
      return;
    }

    secondsRemaining.value = QR_SECONDS_PER_REFRESH;
    try {
      await fetchAndSet();
    } catch (error) {
      // A transient failure on an automatic tick shouldn't kill the loop - the next tick
      // retries. Only the initial/manual fetch (below) surfaces errors to the caller.
    }
  };

  const reset = () => {
    autoRefreshCount.value = 0;
    needsManualRefresh.value = false;
    secondsRemaining.value = QR_SECONDS_PER_REFRESH;
  };

  // `initialQrcode` lets a caller that already has a fresh QR from a prior request (e.g. the
  // instance-creation response) seed the countdown without an extra redundant fetch.
  const start = async (initialQrcode = null) => {
    stop();
    reset();
    if (initialQrcode) {
      qrcodeBase64.value = initialQrcode;
    } else {
      await fetchAndSet();
    }
    tickTimer = setInterval(tick, 1000);
  };

  const manualRefresh = async () => {
    reset();
    await fetchAndSet();
    if (!tickTimer) tickTimer = setInterval(tick, 1000);
  };

  onUnmounted(stop);

  return {
    qrcodeBase64,
    isLoadingQrcode,
    secondsRemaining,
    needsManualRefresh,
    totalSeconds: QR_SECONDS_PER_REFRESH,
    start,
    stop,
    manualRefresh,
  };
}
